/*
 * examProctor.js — webcam proctoring for the Online Exam Portal.
 *
 * Ported from the original exam-proctor.js almost line-for-line. It turns the
 * candidate's camera on, shows a small floating preview, watches for
 * suspicious activity (no face / multiple faces / covered camera / tab
 * switching) and records violations in localStorage under "proctorLog".
 *
 * The original file self-started on DOMContentLoaded on every page that
 * included it (instructions.html, dashboard.html, second_level_exam.html)
 * and was only ever stopped by summary.html. In the React app each of those
 * pages mounts <ExamProctorWidget /> (see components/ExamProctorWidget.js),
 * which calls start() on mount and stop() on unmount — this reproduces the
 * "fresh camera per page" behaviour of the old multi-page site while also
 * cleaning up the widget's injected DOM node when React unmounts it (a
 * static multi-page site never needed that cleanup because the whole
 * document was torn down on every navigation).
 */

const LOG_KEY = "proctorLog";

let stream = null;
let video = null;
let canvas = null;
let ctx = null;
let detector = null;
let timer = null;
let stopped = true;
let lastFrame = null;
let noFaceStreak = 0;
let box = null;
let statusEl = null;
let warnEl = null;
let flashTimer = null;
let visibilityHandlerAttached = false;
let blurHandlerAttached = false;

function log(type, detail) {
  try {
    const arr = JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
    const last = arr[arr.length - 1];
    const now = Date.now();
    if (last && last.type === type && now - last.at < 8000) return;
    arr.push({ type, detail: detail || "", at: now, page: window.location.pathname.split("/").pop() });
    let trimmed = arr;
    if (trimmed.length > 300) trimmed = trimmed.slice(-300);
    localStorage.setItem(LOG_KEY, JSON.stringify(trimmed));
  } catch (e) {
    /* ignore */
  }
  flash(detail || type);
}

/* ---------------- UI ---------------- */
function buildUI() {
  box = document.createElement("div");
  box.id = "proctorCam";
  box.style.cssText =
    "position:fixed;right:14px;bottom:14px;z-index:99998;width:190px;border-radius:10px;" +
    "overflow:hidden;background:#0f172a;box-shadow:0 6px 22px rgba(0,0,0,.35);" +
    "font-family:'Segoe UI',Tahoma,sans-serif;border:2px solid #10b981;";

  video = document.createElement("video");
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.style.cssText =
    "display:block;width:100%;height:140px;object-fit:cover;background:#000;transform:scaleX(-1);";

  statusEl = document.createElement("div");
  statusEl.style.cssText =
    "padding:5px 8px;font-size:.72rem;font-weight:700;color:#e2e8f0;display:flex;align-items:center;gap:6px;";
  statusEl.innerHTML =
    '<span style="width:8px;height:8px;border-radius:50%;background:#10b981;display:inline-block"></span>Proctoring active';

  warnEl = document.createElement("div");
  warnEl.style.cssText = "display:none;padding:5px 8px;font-size:.7rem;font-weight:700;background:#dc2626;color:#fff;";

  box.appendChild(video);
  box.appendChild(statusEl);
  box.appendChild(warnEl);
  document.body.appendChild(box);
}

function flash(msg) {
  if (!warnEl) return;
  warnEl.textContent = "⚠ " + msg;
  warnEl.style.display = "block";
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    if (warnEl) warnEl.style.display = "none";
  }, 4000);
}

function setStatus(ok, text) {
  if (!statusEl || !box) return;
  box.style.borderColor = ok ? "#10b981" : "#dc2626";
  statusEl.innerHTML =
    '<span style="width:8px;height:8px;border-radius:50%;background:' +
    (ok ? "#10b981" : "#dc2626") +
    ';display:inline-block"></span>' +
    text;
}

/* ---------------- camera ---------------- */
function startCamera() {
  if (stream || stopped) return;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    log("camera_unsupported", "Camera not supported by this browser");
    return;
  }
  if (!box) buildUI();
  navigator.mediaDevices
    .getUserMedia({ video: { width: 320, height: 240, facingMode: "user" }, audio: false })
    .then((s) => {
      stream = s;
      video.srcObject = s;
      setStatus(true, "Proctoring active");
      s.getVideoTracks().forEach((t) => {
        t.addEventListener("ended", () => {
          if (!stopped) {
            setStatus(false, "Camera stopped");
            log("camera_stopped", "Camera was turned off");
            retry();
          }
        });
      });
      beginAnalysis();
    })
    .catch((err) => {
      setStatus(false, "Camera blocked");
      log("camera_denied", "Camera access denied (" + (err && err.name) + ")");
      retry();
    });
}

function retry() {
  if (stopped) return;
  stream = null;
  clearInterval(timer);
  setTimeout(startCamera, 5000);
}

/* ---------------- analysis ---------------- */
function beginAnalysis() {
  canvas = document.createElement("canvas");
  canvas.width = 160;
  canvas.height = 120;
  ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (typeof window.FaceDetector === "function") {
    try {
      detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 5 });
    } catch (e) {
      detector = null;
    }
  }

  clearInterval(timer);
  timer = setInterval(tick, 2000);
}

function tick() {
  if (stopped || !video || video.readyState < 2) return;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  let data;
  try {
    data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch (e) {
    return;
  }

  // Covered / dark camera check
  let sum = 0;
  const px = data.data;
  for (let i = 0; i < px.length; i += 40) sum += (px[i] + px[i + 1] + px[i + 2]) / 3;
  const avg = sum / (px.length / 40);
  if (avg < 18) {
    setStatus(false, "Camera covered");
    log("camera_covered", "Camera view is dark or covered");
    return;
  }

  // Face presence (where supported)
  if (detector) {
    detector
      .detect(video)
      .then((faces) => {
        if (!faces || faces.length === 0) {
          noFaceStreak++;
          if (noFaceStreak >= 2) {
            setStatus(false, "No face detected");
            log("no_face", "Candidate not visible in camera");
          }
        } else if (faces.length > 1) {
          noFaceStreak = 0;
          setStatus(false, "Multiple faces");
          log("multiple_faces", faces.length + " people detected in frame");
        } else {
          noFaceStreak = 0;
          setStatus(true, "Proctoring active");
        }
      })
      .catch(() => {
        /* ignore */
      });
  } else {
    // Fallback: frame-difference based presence/motion heuristic
    if (lastFrame) {
      let diff = 0;
      let n = 0;
      for (let j = 0; j < px.length; j += 40) {
        diff += Math.abs(px[j] - lastFrame[j]);
        n++;
      }
      const motion = diff / n;
      if (motion > 45) log("sudden_movement", "Large movement detected in camera");
    }
    lastFrame = new Uint8ClampedArray(px);
    setStatus(true, "Proctoring active");
  }
}

/* ---------------- environment checks ---------------- */
function onVisibilityChange() {
  if (document.hidden && !stopped) log("tab_hidden", "Candidate switched away from the exam");
}
function onWindowBlur() {
  if (!stopped) log("window_blur", "Exam window lost focus");
}

function start() {
  stopped = false;
  if (!visibilityHandlerAttached) {
    document.addEventListener("visibilitychange", onVisibilityChange);
    visibilityHandlerAttached = true;
  }
  if (!blurHandlerAttached) {
    window.addEventListener("blur", onWindowBlur);
    blurHandlerAttached = true;
  }
  startCamera();
}

function stop() {
  stopped = true;
  clearInterval(timer);
  clearTimeout(flashTimer);
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  if (box && box.parentNode) box.parentNode.removeChild(box);
  box = null;
  video = null;
  canvas = null;
  ctx = null;
  detector = null;
  lastFrame = null;
  noFaceStreak = 0;
  if (visibilityHandlerAttached) {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    visibilityHandlerAttached = false;
  }
  if (blurHandlerAttached) {
    window.removeEventListener("blur", onWindowBlur);
    blurHandlerAttached = false;
  }
}

function isActive() {
  return !!stream;
}

function getLog() {
  try {
    return JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
  } catch (e) {
    return [];
  }
}

function clearLog() {
  try {
    localStorage.removeItem(LOG_KEY);
  } catch (e) {
    /* ignore */
  }
}

window.addEventListener("pagehide", () => {
  if (stream) stream.getTracks().forEach((t) => t.stop());
});

/*
 * flag() lets other parts of the app (e.g. the fullscreen shell) record a
 * suspicious-activity entry in the same proctor log used by the camera
 * checks, even on pages where the camera widget isn't mounted (login,
 * summary). It's just the internal logger, exposed — safe to call at any
 * time; if the floating camera widget isn't on screen, log() simply skips
 * the on-screen flash and only writes to localStorage.
 */
function flag(type, detail) {
  log(type, detail);
}

const ExamProctor = { start, stop, isActive, getLog, clearLog, flag };

export default ExamProctor;
